<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Co2CountryAverage } from '@/models/co2-quiz.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import { countries, OneEarth } from '@/models/country.model';
import CategoryField from '@/components/categories/CategoryField.vue';

const store = useStore<ShiftCMSState & { co2: Co2QuizStoreState }>();
const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Co2CountryAverage>,
  },
});

const valid = ref<boolean>(false);

const value = ref<Co2CountryAverage>({
  id: NEW_ID,
  value: 0,
  country: 'NL',
  category: null as any, // eslint-disable-line @typescript-eslint/no-explicit-any
  ...(props.data || {}),
});

const action = computed<string>(() =>
  value.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveCo2CountryAverage = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (value.value.id === NEW_ID) {
      success = await store.dispatch(
        Co2QuizStoreActions.AddCo2Average,
        value.value,
      );
    } else {
      success = await store.dispatch(
        Co2QuizStoreActions.UpdateCo2Average,
        value.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved value',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(Co2QuizStoreActions.GetCo2Averages);
  }
};

const updateLanguage = async () => {
  if (value.value.id === NEW_ID) {
    Object.assign(value.value, { title: '', type: 'value' });
  } else {
    const q = await store.dispatch(
      Co2QuizStoreActions.GetCo2Value,
      value.value.id,
    );

    if (q) {
      Object.assign(value.value, { ...q });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(value.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveCo2CountryAverage">
    <v-container>
      <h2>{{ $t(action) }} a value <CurrentLanguage /></h2>

      <CategoryField class="mt-8" v-model="value.category" :max="1" />

      <v-text-field v-model="value.value" label="Value" required></v-text-field>

      <v-autocomplete
        v-model="value.country"
        :items="[{ text: 'One Earth', value: OneEarth }, ...countries]"
        class="mb-8"
        label="Country"
        item-title="text"
        item-value="value"
        :hide-details="true"
      ></v-autocomplete>

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
